import React from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/utils/cn';
import { ProjectDetail } from '../types/projectDetail.types';

interface ProjectDetailHeaderProps {
  project: ProjectDetail;
}

export function ProjectDetailHeader({ project }: ProjectDetailHeaderProps) {
  const navigate = useNavigate();

  // Helper to format currency
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(value);
  };

  // Helper to get status label and color
  const getStatusInfo = (status: string) => {
    switch (status) {
      case 'active': return { label: 'Đang thực hiện', bg: 'bg-emerald-50', text: 'text-emerald-600' };
      case 'completed': return { label: 'Đã hoàn thành', bg: 'bg-blue-50', text: 'text-blue-600' };
      case 'on_hold': return { label: 'Tạm dừng', bg: 'bg-amber-50', text: 'text-amber-600' };
      case 'planning': return { label: 'Đang chuẩn bị', bg: 'bg-orange-50', text: 'text-orange-600' };
      default: return { label: 'Chưa xác định', bg: 'bg-slate-50', text: 'text-slate-600' };
    }
  };

  const statusInfo = getStatusInfo(project.statusCode);
  const pm = project.members.find(m => m.role === 'Project Manager');

  return (
    <div className="rounded-md bg-white p-4  border border-slate-100">
      <div className="flex flex-col xl:flex-row gap-6">
        {/* Project Thumbnail */}
        <div className="w-full xl:w-72 aspect-[16/10] xl:aspect-auto xl:h-48 rounded-md overflow-hidden bg-slate-100 border border-slate-50 shrink-0">
          {project.thumbnailUrl ? (
            <img src={project.thumbnailUrl} alt={project.name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-slate-300">
              <i className="ri-image-line text-4xl" />
            </div>
          )}
        </div>

        {/* Project Info Grid */}
        <div className="flex-1 min-w-0 space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <h1 className="text-xl lg:text-2xl font-normal text-slate-900 tracking-tight truncate">{project.name}</h1>
              <span className={cn("relative px-2 py-0.5 rounded-md text-xs font-normal transition-all whitespace-nowrap", statusInfo.bg, statusInfo.text)}>
                {statusInfo.label}
              </span>
            </div>
            
            <div className="flex items-center gap-2">
              <button 
                onClick={() => navigate(`/projects/${project.id}/edit`)}
                className="h-9 px-4 rounded-md border border-slate-200 text-xs lg:text-sm font-normal text-slate-600 hover:bg-slate-50 transition-all flex items-center gap-2"
              >
                <i className="ri-edit-line" />
                Chỉnh sửa
              </button>
              <button className="h-9 px-4 rounded-md border border-slate-200 text-xs lg:text-sm font-normal text-slate-600 hover:bg-slate-50 transition-all flex items-center gap-2">
                <i className="ri-download-line" />
                Tải báo cáo
              </button>
              <button className="h-9 px-4 rounded-md bg-emerald-700 text-xs lg:text-sm font-normal text-white hover:bg-emerald-800 transition-all  shadow-emerald-700/20 flex items-center gap-2">
                <i className="ri-add-line" />
                Thêm công việc
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-y-3 gap-x-8">
            <InfoItem icon="ri-hashtag" label="Mã dự án" value={project.code} />
            <InfoItem icon="ri-user-star-line" label="Quản lý dự án" value={pm?.fullName || 'Chưa phân công'} />
            <InfoItem icon="ri-stack-line" label="Loại dự án" value={project.projectType === 'construction' ? 'Thi công' : 'Bảo dưỡng'} />
            <InfoItem icon="ri-calendar-line" label="Ngày khởi công" value={project.startDate || '---'} />
            <InfoItem icon="ri-building-line" label="Chủ đầu tư" value={project.clientName || '---'} />
            <InfoItem icon="ri-calendar-check-line" label="Ngày hoàn thành dự kiến" value={project.endDate || '---'} />
            <InfoItem icon="ri-map-pin-line" label="Địa điểm" value={project.location || '---'} />
            <InfoItem icon="ri-money-dollar-circle-line" label="Tổng giá trị hợp đồng" value={formatCurrency(project.contractAmount)} />
          </div>
        </div>
      </div>
    </div>
  );
}

function InfoItem({ icon, label, value }: { icon: string; label: string; value: string }) {
  return (
    <div className="flex items-center gap-2 min-w-0">
      <div className="w-8 h-8 rounded-md bg-slate-50 flex items-center justify-center shrink-0">
        <i className={cn(icon, "text-slate-400")} />
      </div>
      <div className="flex flex-col min-w-0 leading-tight">
        <span className="text-xs font-normal text-slate-400">{label}:</span>
        <span className="text-xs lg:text-sm font-normal text-slate-700 truncate">{value}</span>
      </div>
    </div>
  );
}
